import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Plus, Users } from 'lucide-react'
import type { Album, MemoirListResponse, MemoirSummary } from '../../../../types'
import { albumRole } from '../lib/album-role'
import { apiClient } from '../lib/api-client'
import { useAlbums, useMemoirs } from '../lib/hooks'
import {
  PURCHASE_RETURN_PARAM,
  readPendingPurchase,
  clearPendingPurchase,
} from '../lib/pending-purchase'
import NewMemoirModal from './new-memoir-modal'
import memoarLogo from '../assets/memoar-logo.png'
import { inviteErrorMessage } from './memoir-handover'
import { formatMemoirDate } from './memoir-payment-notice'

type Invitation = MemoirListResponse['invitations'][number]

/** How long the home waits for a just-paid Memoir before saying so (SOL-204). */
const PURCHASE_POLL_MS = 2000
const PURCHASE_POLL_TRIES = 30

/**
 * Where a signed-in photographer lands (SOL-204): their Memoirs, each the unit
 * they bought, plus "Shared with you" when someone handed albums over to them.
 *
 * Photo counts and covers are summed from the album list rather than asked of
 * the server — GET /api/albums already returns every album with its files.
 */
export default function MemoirsHome(): JSX.Element {
  const navigate = useNavigate()
  const location = useLocation()
  const { memoirs, invitations, loading, error, refresh } = useMemoirs()
  const { albums } = useAlbums()
  const [buying, setBuying] = useState(false)

  const returningFromCheckout =
    new URLSearchParams(location.search).get(PURCHASE_RETURN_PARAM) === 'pending'
  const [purchase, setPurchase] = useState<'idle' | 'waiting' | 'slow'>(
    returningFromCheckout ? 'waiting' : 'idle',
  )

  // Back from Checkout: the Memoir is created by the webhook once payment
  // settles, which can trail the redirect by seconds. Poll for a Memoir that
  // did not exist before checkout, open it, and stop.
  useEffect(() => {
    if (!returningFromCheckout) return
    const pending = readPendingPurchase()
    // No record of what existed before checkout (storage blocked, or a
    // different tab started it): there is no way to tell which Memoir is new,
    // and polling for one would end in a false "taking longer" message while
    // the Memoir sits right there in the list. The list itself still loads.
    if (!pending) {
      setPurchase('idle')
      return
    }
    const known = new Set(pending.knownMemoirIds)
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    let tries = 0

    const tick = async (): Promise<void> => {
      const res = await refresh(true)
      if (cancelled) return
      const fresh = res?.memoirs.find((m) => !known.has(m.uuid))
      if (fresh) {
        clearPendingPurchase()
        navigate(`/memoir/${fresh.uuid}`, { replace: true })
        return
      }
      if (++tries >= PURCHASE_POLL_TRIES) {
        setPurchase('slow')
        return
      }
      timer = setTimeout(() => void tick(), PURCHASE_POLL_MS)
    }
    void tick()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [returningFromCheckout, refresh, navigate])

  // "Back to album" from the export page lands here with the album to reopen;
  // forward it to the workspace that album lives in.
  const openAlbumId = (location.state as { openAlbumId?: string } | null)?.openAlbumId
  useEffect(() => {
    if (!openAlbumId) return
    const album = albums.find((a) => a.uuid === openAlbumId)
    if (!album) return
    const target = album.memoirId && albumRole(album) === 'owner'
      ? `/memoir/${album.memoirId}`
      : '/shared'
    navigate(target, { replace: true, state: { openAlbumId } })
  }, [openAlbumId, albums, navigate])

  const byMemoir = useMemo(() => {
    const map = new Map<string, Album[]>()
    for (const a of albums) {
      if (!a.memoirId || albumRole(a) !== 'owner') continue
      map.set(a.memoirId, [...(map.get(a.memoirId) ?? []), a])
    }
    return map
  }, [albums])

  // The server's role (SOL-185): a claimant, or the Memoir's accepted invitee.
  const sharedCount = albums.filter((a) => albumRole(a) === 'member').length

  if (loading && memoirs.length === 0) {
    return <div className="memoirs-home memoirs-home--loading"><p>Loading your Memoirs…</p></div>
  }

  if (error && memoirs.length === 0) {
    return (
      <div className="memoirs-home memoirs-home--error">
        <p>We couldn't load your Memoirs.</p>
        <button className="memoirs-home__retry" onClick={() => void refresh()}>Try again</button>
      </div>
    )
  }

  return (
    <div className="memoirs-home">
      <h1 className="memoirs-home__title">Your Memoirs</h1>

      {purchase === 'waiting' && (
        <p className="memoirs-home__purchase" role="status">
          Payment received. Preparing your new Memoir…
        </p>
      )}
      {purchase === 'slow' && (
        <p className="memoirs-home__purchase" role="status">
          Your payment went through. Your new Memoir is taking a little longer than usual
          and will appear here shortly. Refresh in a minute if it doesn't.
        </p>
      )}

      {memoirs.length === 0 && purchase === 'idle' && (
        <p className="memoirs-home__intro">
          A Memoir is a memoir in photographs: one complete delivery to one client, kept safe for
          them. Start your first one below.
        </p>
      )}

      <div className="memoirs-home__grid">
        {memoirs.map((m) => (
          <MemoirCard
            key={m.uuid}
            memoir={m}
            albums={byMemoir.get(m.uuid) ?? []}
            onOpen={() => navigate(`/memoir/${m.uuid}`)}
          />
        ))}

        {invitations.map((i) => i.acceptedAt ? (
          <button key={i.uuid} className="memoir-card memoir-card--shared" onClick={() => navigate(`/memoir/${i.uuid}`)}>
            <div className="memoir-card__cover memoir-card__cover--icon" aria-hidden="true">
              <Users size={32} strokeWidth={1.5} />
            </div>
            <div className="memoir-card__body">
              <span className="memoir-card__name">{i.name}</span>
              <span className="memoir-card__meta">Shared with you · view only</span>
            </div>
          </button>
        ) : (
          <InvitationCard key={i.uuid} invitation={i} onChanged={() => void refresh(true)} />
        ))}

        {/* Always shown, even empty: /shared is where "Add Album via Code"
            lives, and a client handed a code owns no Memoir -- without this
            card their home would offer no way to redeem it. */}
        <button className="memoir-card memoir-card--shared" onClick={() => navigate('/shared')}>
          <div className="memoir-card__cover memoir-card__cover--icon" aria-hidden="true">
            <Users size={32} strokeWidth={1.5} />
          </div>
          <div className="memoir-card__body">
            <span className="memoir-card__name">Shared with you</span>
            <span className="memoir-card__meta">
              {sharedCount === 0
                ? 'Have a code? Add an album'
                : sharedCount === 1 ? '1 album' : `${sharedCount} albums`}
            </span>
          </div>
        </button>

        <button className="memoir-card memoir-card--new" onClick={() => setBuying(true)}>
          <div className="memoir-card__cover memoir-card__cover--icon" aria-hidden="true">
            <Plus size={32} strokeWidth={1.5} />
          </div>
          <div className="memoir-card__body">
            <span className="memoir-card__name">New Memoir</span>
            <span className="memoir-card__meta">€30 + VAT, paid once</span>
          </div>
        </button>
      </div>

      {buying && (
        <NewMemoirModal knownMemoirIds={memoirs.map((m) => m.uuid)} onClose={() => setBuying(false)} />
      )}
    </div>
  )
}

function InvitationCard({ invitation, onChanged }: { invitation: Invitation; onChanged: () => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  async function answer(accept: boolean): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      if (accept) await apiClient.acceptMemoirInvite(invitation.uuid)
      else await apiClient.declineMemoirInvite(invitation.uuid)
      onChanged()
    } catch (e) {
      setError(inviteErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="memoir-card memoir-card--invitation">
      <div className="memoir-card__body">
        <span className="memoir-card__name">{invitation.name}</span>
        <span className="memoir-card__meta">Your photographer wants to hand this Memoir over to you</span>
        <div className="memoir-card__actions">
          <button type="button" className="memoir-payment__action" disabled={busy} onClick={() => void answer(true)}>Accept</button>{' '}
          <button type="button" className="memoir-payment__refund" disabled={busy} onClick={() => void answer(false)}>Decline</button>
        </div>
        {error && <p className="memoir-payment__error" role="alert">{error}</p>}
      </div>
    </div>
  )
}

function MemoirCard({ memoir, albums, onOpen }: { memoir: MemoirSummary; albums: Album[]; onOpen: () => void }) {
  const photoCount = albums.reduce((sum, a) => sum + (a.files?.length ?? 0), 0)
  // The first album with a cover lends it to the Memoir. The file object, not
  // just its id: the signed URL needs its sig and exp (SOL-135, SOL-157).
  const coverAlbum = albums.find((a) => !a.isEmpty && a.resolvedCoverFileId)
  const coverFile = coverAlbum?.files?.find((f) => f.id === coverAlbum.resolvedCoverFileId)
  const coverUrl = coverAlbum && coverFile
    ? apiClient.getVariantUrl(coverAlbum.uuid, coverFile, 'cover')
    : null
  const readOnly = memoir.status === 'grace'

  return (
    <button className={`memoir-card${readOnly ? ' memoir-card--readonly' : ''}`} onClick={onOpen}>
      <div className="memoir-card__cover">
        {coverUrl ? (
          <img src={coverUrl} alt="" loading="lazy" />
        ) : (
          <span
            className="memoir-card__mark"
            style={{ ['--cover-mask' as string]: `url(${memoarLogo})` } as React.CSSProperties}
            aria-hidden="true"
          />
        )}
      </div>
      <div className="memoir-card__body">
        <span className="memoir-card__name">{memoir.name}</span>
        <span className="memoir-card__meta">
          {memoir.lapsed && memoir.deletionDueAt
            ? `Deleted on ${formatMemoirDate(memoir.deletionDueAt)} unless renewed`
            : memoir.deletionDueAt
            ? `Deleted on ${formatMemoirDate(memoir.deletionDueAt)} unless paid again`
            : readOnly
            ? 'Uploads paused'
            : !memoir.writable
            ? `Read-only · paid until ${formatMemoirDate(memoir.paidUntil)}`
            : photoCount === 0
              ? 'No photos yet'
              : photoCount === 1 ? '1 photo' : `${photoCount} photos`}
        </span>
      </div>
    </button>
  )
}
